import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Button } from '@leadsales/design-system/react';
import type { tTourStep } from './Tour.types';
import './Tour.styles.scss';

type tTourProps = {
  step: tTourStep | null;
  targetRect: DOMRect | null;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  nextLabel: string;
  backLabel: string;
  skipLabel: string;
  doneLabel: string;
  stepLabel?: string;
};

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 300;
const TOOLTIP_GAP = 14;
const VIEWPORT_MARGIN = 12;

// Four dimming strips around the padded target rect instead of a single
// full-screen backdrop — this leaves a true hole with nothing painted over
// the target, so its real element stays pixel-for-pixel visible without
// relying on z-index tricks that a nested stacking context could break.
function computeSpotlightStrips(rect: DOMRect | null) {
  if (!rect) return null;
  const top = Math.max(rect.top - SPOTLIGHT_PADDING, 0);
  const bottom = rect.bottom + SPOTLIGHT_PADDING;
  const left = Math.max(rect.left - SPOTLIGHT_PADDING, 0);
  const right = rect.right + SPOTLIGHT_PADDING;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return {
    holeTop: top,
    holeLeft: left,
    holeWidth: right - left,
    holeHeight: bottom - top,
    strips: [
      { top: 0, left: 0, width: vw, height: top },
      { top: bottom, left: 0, width: vw, height: Math.max(vh - bottom, 0) },
      { top, left: 0, width: left, height: bottom - top },
      {
        top,
        left: right,
        width: Math.max(vw - right, 0),
        height: bottom - top,
      },
    ],
  };
}

// Presses product click-outside handlers listen for (see the root effect).
const OUTSIDE_PRESS_EVENTS = ['mousedown', 'pointerdown', 'touchstart'] as const;
const stopOutsidePress = (event: Event) => event.stopPropagation();

const GUIDE_PADDING = 6;
// The touch cursor's diameter; its centre is what gets positioned. Half of
// the original 32px: a fingertip-sized marker that no longer hides the
// control it points at (AGI-498 critique).
const CURSOR_SIZE = 16;
// Below this width or height a target is a tab, an icon button or a chip:
// the cursor sits on its bottom-right corner so the label or icon stays
// visible next to the dot.
const SMALL_TARGET = 64;
// A short target at least this wide is a row (a menu item): the next row
// starts at its bottom edge (GRO-1247).
const ROW_TARGET_WIDTH = 160;
// How far inside a row's right edge the cursor's centre sits: the dot stays
// whole inside the row.
const ROW_CURSOR_INSET = 14;

// Where the cursor's centre lands on a tapped target: low and right of
// centre on a roomy target, so it never covers the words the person is being
// asked to read; the bottom-right corner of a small one. On a row the corner
// would hang the dot over the row below (in the stage menu, "Eliminar
// etapa"), so it sits at the row's right end, vertically centred.
function cursorCentre(rect: DOMRect): { x: number; y: number } {
  if (rect.height < SMALL_TARGET && rect.width >= ROW_TARGET_WIDTH) {
    return { x: rect.right - ROW_CURSOR_INSET, y: rect.top + rect.height / 2 };
  }
  if (rect.width < SMALL_TARGET || rect.height < SMALL_TARGET) {
    // Centred on the corner itself: three quarters of the dot hang outside,
    // so even a 40px icon button keeps most of its icon.
    return { x: rect.right - 2, y: rect.bottom - 2 };
  }
  return { x: rect.left + rect.width * 0.6, y: rect.top + rect.height * 0.55 };
}

// A still "from here to there" for steps whose action ends somewhere else
// (a drag to a column, a drop into a tab): a dotted path from the spotlight
// hole to the destination plus a dashed outline around it. No motion on
// purpose — the DS motion contract has no pattern for gesture hints yet, and
// a static connector needs none. Leaves from whichever side of the hole
// faces the destination and lands on the facing side of the destination.
function computeGuide(
  hole: { top: number; left: number; width: number; height: number },
  dest: DOMRect,
) {
  if (dest.width === 0 && dest.height === 0) return null;
  const hx = hole.left + hole.width / 2;
  const hy = hole.top + hole.height / 2;
  const dx = dest.left + dest.width / 2;
  const dy = dest.top + dest.height / 2;
  const horizontal = Math.abs(dx - hx) >= Math.abs(dy - hy);

  const start = horizontal
    ? { x: dx > hx ? hole.left + hole.width : hole.left, y: hy }
    : { x: hx, y: dy > hy ? hole.top + hole.height : hole.top };
  const end = horizontal
    ? {
        x: dx > hx ? dest.left - GUIDE_PADDING : dest.right + GUIDE_PADDING,
        y: dy,
      }
    : {
        x: dx,
        y: dy > hy ? dest.top - GUIDE_PADDING : dest.bottom + GUIDE_PADDING,
      };

  const c1 = horizontal
    ? { x: (start.x + end.x) / 2, y: start.y }
    : { x: start.x, y: (start.y + end.y) / 2 };
  const c2 = horizontal
    ? { x: (start.x + end.x) / 2, y: end.y }
    : { x: end.x, y: (start.y + end.y) / 2 };

  return {
    path: `M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${end.x} ${end.y}`,
    outline: {
      x: dest.left - GUIDE_PADDING,
      y: dest.top - GUIDE_PADDING,
      width: dest.width + GUIDE_PADDING * 2,
      height: dest.height + GUIDE_PADDING * 2,
    },
  };
}

// The drag ghost starts exactly over the target and ends centred under the
// destination's bottom edge (a column header's drop zone), so the loop reads
// "pick this up, put it there". The delta travels through two CSS custom
// properties the keyframes consume; everything else is geometry.
function computeGhost(target: DOMRect, dest: DOMRect) {
  const endLeft = dest.left + dest.width / 2 - target.width / 2;
  const endTop = dest.bottom + GUIDE_PADDING + 2;

  return {
    top: target.top,
    left: target.left,
    width: target.width,
    height: target.height,
    dx: endLeft - target.left,
    dy: endTop - target.top,
  };
}

export default function Tour(props: tTourProps) {
  const {
    step,
    targetRect,
    stepIndex,
    totalSteps,
    onNext,
    onPrev,
    onSkip,
    nextLabel,
    backLabel,
    skipLabel,
    doneLabel,
    stepLabel,
  } = props;

  const tooltipRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const spotlight = computeSpotlightStrips(targetRect);

  useLayoutEffect(() => {
    if (!step || !targetRect) {
      setTooltipPos(null);
      return;
    }
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 160;
    let top = 0;
    let left = 0;

    if (step.placement === 'right') {
      top = targetRect.top;
      left = targetRect.right + TOOLTIP_GAP;
    } else if (step.placement === 'left') {
      top = targetRect.top;
      left = targetRect.left - TOOLTIP_WIDTH - TOOLTIP_GAP;
    } else if (step.placement === 'top') {
      top = targetRect.top - tooltipHeight - TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;
    } else {
      top = targetRect.bottom + TOOLTIP_GAP;
      left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2;
    }

    top = Math.min(
      Math.max(top, VIEWPORT_MARGIN),
      window.innerHeight - tooltipHeight - VIEWPORT_MARGIN,
    );
    left = Math.min(
      Math.max(left, VIEWPORT_MARGIN),
      window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN,
    );

    setTooltipPos({ top, left });
  }, [step, targetRect]);

  // The drag ghost is a copy of the real card, so the demo shows *that* lead
  // lifting off, not an empty box over it. Hooks and ids come off the copy so
  // no selector — the tour's own `getTarget` included — can ever pick it up.
  // Keyed on tooltipPos as well: the ghost only mounts once the tooltip has
  // been placed, and step/targetRect are unchanged by then.
  useLayoutEffect(() => {
    const host = ghostRef.current;
    if (!host) return;
    host.replaceChildren();
    const source = step?.gesture === 'drag' ? step.getTarget() : null;
    if (!source) return;
    const copy = source.cloneNode(true) as HTMLElement;
    [copy, ...copy.querySelectorAll('*')].forEach((el) => {
      el.removeAttribute('id');
      el.removeAttribute('data-testid');
      el.removeAttribute('data-tour');
      el.removeAttribute('tabindex');
    });
    copy.classList.add('TourGuide__GhostCopy');
    host.appendChild(copy);
  }, [step, targetRect, tooltipPos]);

  // Keyboard and screen-reader users land in the card on every step (AGI-526
  // I3), on its primary button so Enter walks the tour. Only when focus is
  // nowhere (the panel that started the tour just unmounted) or already in
  // the card: a step that advances on the person's own action has put focus
  // on a real control — the next field of a form, a focus-driven menu that
  // closes on blur — and taking it away would undo what they just did.
  const hasCard = !!tooltipPos;
  useEffect(() => {
    if (!hasCard) return;
    const card = tooltipRef.current;
    const active = document.activeElement;
    const focusIsFree = !active || active === document.body || !!card?.contains(active);
    if (focusIsFree) card?.querySelector<HTMLButtonElement>('[data-tour-primary]')?.focus();
  }, [step?.id, hasCard]);

  // The tour's own UI must never count as a click outside the product's
  // menus. The chat plus menu closes on any document mousedown outside it, so
  // pressing "Siguiente" closed the very menu the next step opens from, and
  // the respond tour lost its last step (AGI-498). Stopped natively at the
  // tour root: the card and the backdrop strips are the only parts that
  // catch pointer events, and a click in the spotlight hole reaches the real
  // control without passing through here. Cleanup still closes menus: onEnd
  // dispatches its own mousedown on document.body.
  // A callback ref, not an effect: the root unmounts between steps and a new
  // element mounts, and each one needs the listeners.
  const rootRef = useCallback((root: HTMLDivElement | null) => {
    if (!root) return;
    OUTSIDE_PRESS_EVENTS.forEach((name) => root.addEventListener(name, stopOutsidePress));
  }, []);

  if (!step || !targetRect || !tooltipPos || !spotlight) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const title = typeof step.title === 'function' ? step.title() : step.title;
  const body = typeof step.body === 'function' ? step.body() : step.body;
  const guideRect = step.guideTo?.()?.getBoundingClientRect() ?? null;
  const guide = guideRect
    ? computeGuide(
        {
          top: spotlight.holeTop,
          left: spotlight.holeLeft,
          width: spotlight.holeWidth,
          height: spotlight.holeHeight,
        },
        guideRect,
      )
    : null;
  const ghost =
    step.gesture === 'drag' && guide && guideRect
      ? computeGhost(targetRect, guideRect)
      : null;
  const ghostStyle = ghost
    ? ({
        top: ghost.top,
        left: ghost.left,
        width: ghost.width,
        height: ghost.height,
        '--tour-guide-dx': `${ghost.dx}px`,
        '--tour-guide-dy': `${ghost.dy}px`,
      } as CSSProperties)
    : undefined;
  // The cursor rides the ghost on a drag; on a tap it lands per cursorCentre.
  const tapCentre = step.gesture === 'tap' ? cursorCentre(targetRect) : null;
  const cursorStyle: CSSProperties | undefined = ghost
    ? ({
        top: ghost.top + ghost.height * 0.55 - CURSOR_SIZE / 2,
        left: ghost.left + ghost.width * 0.55 - CURSOR_SIZE / 2,
        '--tour-guide-dx': `${ghost.dx}px`,
        '--tour-guide-dy': `${ghost.dy}px`,
      } as CSSProperties)
    : tapCentre
      ? {
          top: tapCentre.y - CURSOR_SIZE / 2,
          left: tapCentre.x - CURSOR_SIZE / 2,
        }
      : undefined;

  return createPortal(
    // The root spans the viewport for stacking only: pointer events pass
    // through it so the real control inside the spotlight hole stays
    // clickable (advance-on-action). Only the strips and the card catch them.
    <div
      ref={rootRef}
      className="fixed inset-0 z-[1000] pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* The dimmed strips block clicks outside the spotlight but do not
          skip: with steps advancing on the person's own clicks, a mis-click
          on the backdrop must not throw the tour away. Saltar and Escape
          are the exits (NN heuristic 3). */}
      {spotlight.strips.map((strip, i) => (
        <div
          key={i}
          aria-hidden="true"
          data-testid="tour-backdrop"
          className="absolute bg-black/55 pointer-events-auto"
          style={{
            top: strip.top,
            left: strip.left,
            width: strip.width,
            height: strip.height,
          }}
        />
      ))}

      <div
        data-testid="tour-spotlight"
        className={`absolute rounded-lg ring-2 ring-primary pointer-events-none transition-[top,left,width,height] duration-150`}
        style={{
          top: spotlight.holeTop,
          left: spotlight.holeLeft,
          width: spotlight.holeWidth,
          height: spotlight.holeHeight,
        }}
      />

      {guide && (
        <svg
          className={`absolute inset-0 w-full h-full pointer-events-none text-primary TourGuide__Connector ${ghost ? 'TourGuide__Connector--fallback' : ''}`}
          aria-hidden="true"
          data-testid="tour-guide"
        >
          <defs>
            <marker
              id="tour-guide-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="8"
              markerHeight="8"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
            </marker>
          </defs>
          <path
            d={guide.path}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4 5"
            strokeLinecap="round"
            markerEnd="url(#tour-guide-arrow)"
          />
          <rect
            x={guide.outline.x}
            y={guide.outline.y}
            width={guide.outline.width}
            height={guide.outline.height}
            rx="8"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4 5"
          />
        </svg>
      )}

      {ghost && (
        <div
          ref={ghostRef}
          className="TourGuide__Ghost"
          aria-hidden="true"
          data-testid="tour-ghost"
          style={ghostStyle}
        />
      )}
      {cursorStyle && (
        <div
          className={`TourGuide__Cursor ${ghost ? 'TourGuide__Cursor--drag' : 'TourGuide__Cursor--tap'}`}
          aria-hidden="true"
          data-testid="tour-cursor"
          style={cursorStyle}
        >
          <span className="TourGuide__Ripple" />
        </div>
      )}

      {/* The card speaks the DS (AGI-526): --ls-* tokens in Tour.styles.scss
          and the DS buttons. Only its width stays a Tailwind literal, because
          the placement maths above reads TOOLTIP_WIDTH. */}
      <div
        ref={tooltipRef}
        className="TourCard absolute w-[300px] pointer-events-auto"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        {stepLabel && <p className="TourCard__Eyebrow">{stepLabel}</p>}
        <p className="TourCard__Title">{title}</p>
        <p className="TourCard__Body">{body}</p>
        {/* Two rows (INT-1177), not one: a tour with many steps (17, vs the
            5-step builder tour this component also serves) pushes the dot
            row wide enough that cramming "Saltar" + "Anterior" + "Siguiente"
            onto the same line as the dots overflows the fixed 300px card.
            Matches the validated prototype's footer-top/actions split. */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 flex-wrap">
              {Array.from({ length: totalSteps }, (_, i) => (
                <span
                  key={i}
                  className={classNames('TourCard__Dot', {
                    'TourCard__Dot--active': i === stepIndex,
                  })}
                />
              ))}
            </div>
            <Button
              type="button"
              color="ghost"
              size="xs"
              className="flex-none"
              onClick={onSkip}
            >
              {skipLabel}
            </Button>
          </div>
          {/* The alternative as tertiary, the primary last (composition.md). */}
          <div className="flex items-center justify-end gap-2">
            {!isFirst && (
              <Button type="button" color="tertiary" size="xs" onClick={onPrev}>
                {backLabel}
              </Button>
            )}
            <Button
              type="button"
              color="primary"
              size="xs"
              data-tour-primary
              onClick={onNext}
            >
              {isLast ? doneLabel : nextLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
